import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import { DateUtils, PermissionArea } from '@pbt/pbt-ui-components'
import type { RowData } from '@pbt/pbt-ui-components/src/components/tables/RhapTable'

import type { PopperAction } from '~/components/common/ActionsPopper'
import FeatureToggle from '~/constants/featureToggle'
import InvoiceType from '~/constants/InvoiceType'
import { OrderType } from '~/constants/SOAPStates'
import { usePrescriptionsSubscription } from '~/hooks/usePrescriptionsSubscription'
import {
  fetchChargesByLog,
  getCharges,
  getChargesLoading,
} from '~/store/duck/charges'
import { getCRUDByArea, getCurrentBusinessId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getSoapId } from '~/store/reducers/soap'
import type { Prescription } from '~/types'
import { getPrescriptionType } from '~/utils/prescription'
import {
  getIdFromTimelineItem,
  getPrescriptionCanBeRefilled,
  getRemainingRefillsCount,
} from '~/utils/prescriptionUtils'

import { RIGHT_RAIL_DATE_COL_PX } from '../constants'
import PatientHistoryEmptyState from '../PatientHistoryEmptyState'
import PatientHistoryWidget from '../PatientHistoryWidget'
import PrescriptionActionsCell from './PrescriptionActionsCell'
import PrescriptionWorkflowCell from './PrescriptionWorkflowCell'
import usePrescriptions from './usePrescriptions'

const ITEMS_PER_PAGE = 5

const PrescriptionsSummaryWidget = ({
  clientId,
  patientId,
}: {
  clientId: string
  patientId: string
}) => {
  const { t } = useTranslation(['Common', 'Soap', 'Clients'])
  const dispatch = useDispatch()

  const [currentPage, setCurrentPage] = useState(1)

  const {
    handleGoToSoap,
    handleGoToInvoice,
    handleNavigateToItemEditableDetails,
    handlePrint,
    handleDispenseRefill,
    handleCancelRefills,
  } = usePrescriptions({ clientId, patientId })

  const currentBusinessId = useSelector(getCurrentBusinessId)
  const charges = useSelector(getCharges)
  const chargesLoading = useSelector(getChargesLoading)
  const currentSoapId = useSelector(getSoapId)

  const { read: invoiceReadPermissions, update: invoiceUpdatePermissions } =
    useSelector(getCRUDByArea(PermissionArea.INVOICE))

  const { read: soapReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.SOAP),
  )

  const { update: prescriptionUpdatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.PRESCRIPTION),
  )

  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const {
    prescriptions,
    totalCount,
    loading: isLoading,
  } = usePrescriptionsSubscription(
    patientId,
    (currentPage - 1) * ITEMS_PER_PAGE, // Calculate offset based on current page
    ITEMS_PER_PAGE,
  )

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const getActions = (prescription?: Prescription, refetchEntity?: boolean) => {
    if (!prescription) {
      return []
    }

    if (refetchEntity) {
      dispatch(
        fetchChargesByLog({
          logId: R.path(['id'], prescription),
          logType: OrderType.PRESCRIPTION,
        }),
      )
    }

    const isCurrentContextItem =
      !isPatientSharingEnabled || prescription.businessId === currentBusinessId
    const isInvoice = prescription.invoice?.type === InvoiceType.INVOICE

    const onCancelAllRefills: (() => void) | undefined =
      invoiceUpdatePermissions
        ? () => handleCancelRefills(prescription)
        : undefined

    const canEditViaInvoice = invoiceUpdatePermissions && !prescription.soapId
    const canEditViaPrescription =
      Boolean(prescription.soapId) && prescriptionUpdatePermissions
    const canEdit = canEditViaInvoice || canEditViaPrescription
    const onEdit = canEdit
      ? () =>
          handleNavigateToItemEditableDetails({
            soapId: prescription.soapId || '',
            prescription,
            businessId: prescription.businessId,
          })
      : undefined
    const isEditDisabled = prescription.soapId
      ? !prescriptionUpdatePermissions
      : !invoiceUpdatePermissions

    return [
      isCurrentContextItem && {
        id: 'edit',
        label: t('Common:EDIT_ACTION'),
        onClick: onEdit,
        disabled: isEditDisabled,
      },
      isCurrentContextItem &&
        getPrescriptionType(prescription.prescriptionType)?.isInHouse && {
          id: 'print',
          label: t('Common:PRINT_ACTION'),
          onClick: invoiceReadPermissions
            ? () => handlePrint(prescription)
            : undefined,
          disabled: !invoiceReadPermissions,
        },
      isCurrentContextItem &&
        Boolean(getIdFromTimelineItem(prescription)) && {
          id: 'goToInvoice',
          label: chargesLoading
            ? ''
            : isInvoice
              ? t('Common:GO_TO_INVOICE')
              : t('Common:GO_TO_CHARGE_SHEET'),
          onClick: invoiceReadPermissions
            ? () => handleGoToInvoice(prescription)
            : undefined,
          disabled: !invoiceReadPermissions,
        },
      prescription.soapId &&
        !prescription.isOTC &&
        prescription.soapId !== currentSoapId && {
          id: 'goToSoap',
          label: t('Common:VIEW_SOAP'),
          onClick: soapReadPermissions
            ? () =>
                handleGoToSoap(prescription.soapId!, prescription.businessId)
            : undefined,
          disabled: !soapReadPermissions,
        },
      isCurrentContextItem &&
        getPrescriptionCanBeRefilled(prescription) && {
          id: 'dispenseRefill',
          label: t('Common:DISPENSE_REFILL'),
          onClick: invoiceUpdatePermissions
            ? () => handleDispenseRefill(prescription)
            : undefined,
          disabled: !invoiceUpdatePermissions,
        },
      isCurrentContextItem &&
        getPrescriptionCanBeRefilled(prescription) && {
          id: 'cancelRefills',
          label: t('Soap:PRESCRIPTIONS_WIDGET.CANCEL_ALL_REFILLS'),
          onClick: onCancelAllRefills,
          disabled: !invoiceUpdatePermissions,
        },
    ].filter(Boolean) as PopperAction[]
  }

  const columns = [
    {
      id: 'prescribed',
      header: t('Common:PRESCRIBED'),
      renderCell: (prescription: RowData) =>
        DateUtils.formatDate((prescription as Prescription).prescribedDate),
      fixedWidth: RIGHT_RAIL_DATE_COL_PX,
    },
    {
      id: 'item',
      header: t('Common:ITEM'),
      renderCell: (prescription: RowData) =>
        (prescription as Prescription).name,
      width: 1.5,
    },
    {
      id: 'workflow',
      header: t('Common:WORKFLOW_ONE'),
      renderCell: (prescription: RowData) => (
        <PrescriptionWorkflowCell
          workflowType={(prescription as Prescription).prescriptionType}
        />
      ),
      width: 1.2,
    },
    {
      id: 'refills',
      header: t('Common:REFILL_OTHER'),
      renderCell: (prescription: RowData) => {
        const isPrescriptionCancelled = Boolean(prescription.cancelledBy)
        const refills = getRemainingRefillsCount(prescription as Prescription)

        if (isPrescriptionCancelled) {
          return <span>{t('Common:CANCELLED')}</span>
        }
        return <span>{refills}</span>
      },
      width: 0.6,
    },
    {
      id: 'actions',
      renderCell: (prescription: RowData) => (
        <PrescriptionActionsCell
          actions={getActions?.(prescription as Prescription)}
        />
      ),
      width: 0.5,
      clamp: 0,
    },
  ]

  return (
    <PatientHistoryWidget
      columns={columns}
      currentPage={currentPage}
      data={prescriptions}
      isLoading={isLoading}
      maxRows={ITEMS_PER_PAGE}
      noRecords={
        <PatientHistoryEmptyState
          message={t('Clients:SNAPSHOTS_AND_RECORDS.NO_PRESCRIPTIONS')}
        />
      }
      title={t('Soap:PRESCRIPTIONS_WIDGET.TITLE')}
      totalCount={totalCount}
      onPageChange={handlePageChange}
    />
  )
}

export default PrescriptionsSummaryWidget
